import { useState, useEffect, useCallback } from 'react';

const navLinks = [
  { label: 'Hjem', href: '#hero' },
  { label: 'Tjenester', href: '#services' },
  { label: 'Om Oss', href: '#about' },
  { label: 'Kontakt', href: '#footer' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 60);
      if (menuOpen) setMenuOpen(false);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [menuOpen]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const handleNav = useCallback((href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 50);
    }
  }, []);

  const handleBooking = useCallback(() => {
    setMenuOpen(false);
    window.open('https://system.easypractice.net/book/equi#choose-where', '_blank');
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          scrolled || menuOpen
            ? 'bg-[#0e0a07]/98 backdrop-blur-md'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a
            href="#hero"
            onClick={(e) => { e.preventDefault(); handleNav('#hero'); }}
            className="flex items-center gap-2.5 cursor-pointer flex-shrink-0"
            aria-label="Equi AS – tilbake til toppen"
          >
            <img
              src="https://storage.readdy-site.link/project_files/f7a91cb2-16ed-4f1e-969d-5ece724c23c9/b03cb20b-4fb4-4837-9a55-89db62a7f1e0_equiIconPlain.png?v=4f72fd85e284e63ce2e4f62caa853a60"
              alt="Equi AS logo"
              className="w-8 h-8 md:w-10 md:h-10 object-contain"
              width="40"
              height="40"
            />
            <div>
              <span className="text-white font-bold text-base md:text-xl tracking-widest uppercase leading-none">Equi AS</span>
              <p className="text-[#c9a84c] text-[9px] md:text-[10px] tracking-[0.2em] uppercase mt-0.5 hidden sm:block">Skjønnhet og Velvære</p>
            </div>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1" aria-label="Hovednavigasjon">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => handleNav(link.href)}
                className="px-4 py-2 text-sm tracking-wide text-white/80 hover:text-[#c9a84c] transition-colors duration-200 rounded-full cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </button>
            ))}
            <button
              onClick={handleBooking}
              className="ml-3 px-5 py-2.5 bg-[#c9a84c] text-[#0e0a07] font-semibold rounded-full hover:bg-[#e0c06a] transition-all duration-300 text-sm tracking-wide whitespace-nowrap cursor-pointer"
            >
              Bestill time
            </button>
          </nav>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden flex flex-col justify-center items-center w-10 h-10 cursor-pointer rounded-lg hover:bg-white/5 transition-colors flex-shrink-0"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Lukk meny' : 'Åpne meny'}
            aria-expanded={menuOpen}
          >
            <span
              className={`block w-5 h-0.5 bg-white transition-all duration-300 origin-center ${
                menuOpen ? 'rotate-45 translate-y-[3px]' : '-translate-y-[3px]'
              }`}
            />
            <span
              className={`block w-5 h-0.5 bg-white transition-all duration-300 ${
                menuOpen ? 'opacity-0 scale-x-0' : 'opacity-100'
              }`}
            />
            <span
              className={`block w-5 h-0.5 bg-white transition-all duration-300 origin-center ${
                menuOpen ? '-rotate-45 -translate-y-[3px]' : 'translate-y-[3px]'
              }`}
            />
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-all duration-300 ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      </div>

      {/* Mobile Menu Panel */}
      <div
        className={`fixed top-16 left-0 right-0 z-40 md:hidden bg-[#0e0a07]/98 backdrop-blur-md transition-all duration-300 ${
          menuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
        }`}
      >
        <nav className="flex flex-col px-4 py-4 gap-1" aria-label="Mobilnavigasjon">
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={() => handleNav(link.href)}
              className="text-left px-4 py-4 text-base tracking-wide text-white/80 hover:text-[#c9a84c] hover:bg-white/5 rounded-xl transition-colors cursor-pointer w-full"
            >
              {link.label}
            </button>
          ))}
          <div className="pt-2 pb-2">
            <button
              onClick={handleBooking}
              className="w-full px-4 py-4 bg-[#c9a84c] text-[#0e0a07] font-semibold rounded-xl text-base tracking-wide cursor-pointer whitespace-nowrap hover:bg-[#e0c06a] transition-colors"
            >
              Bestill time online
            </button>
          </div>
          <div className="border-t border-white/8 pt-4 pb-2 px-4">
            <a href="tel:+4746884700" className="flex items-center gap-3 text-white/50 text-sm hover:text-[#c9a84c] transition-colors">
              <i className="ri-phone-line text-[#c9a84c]" />
              +47 468 84 700
            </a>
          </div>
        </nav>
      </div>
    </>
  );
}